/**
 * GameControl - Main Game View Component
 *
 * REFACTORED VERSION - Clean, maintainable, well-documented
 * Uses service modules for separation of concerns
 */

import { Button, Typography, Card, CardContent } from "@mui/material";
import Box, { BoxProps } from "@mui/material/Box";
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Field, Game, GameSeries, Stone, StoneType } from "../models";
import { addGameToLocalStorage, createRematch } from "../utils/common";
import { persistLocalStateToCloud } from "../utils/playerState";
import { GameInfoHeaderControl } from "./Game/GameInfoHeaderControl";
import { GameBoardControl } from "./Game/GameBoardControl";
import { RemainingStonesControl, SelectStoneDialog } from "./SelectStoneDialog";
import { ShareGameLinkControl } from "./ShareGameLinkControl";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import { CreateGameDialog } from "../components/CreateGameDialog";
import { ClaimGameHint } from "../components/ClaimGameHint";
import { Confetti } from "../components/Confetti";

// Import our clean services
import { logger } from "../services/debugLogger";
import { hasWon, getNextPlayer, getPlayerName } from "../services/gameLogic";
import {
  updateGameInDB,
  getGameById,
  getAllStones,
  getGameSeriesById,
  subscribeToGame,
} from "../services/datastoreService";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";

// ============================================================================
// TYPES
// ============================================================================

interface GameProps {}

export interface BoardField extends Field {
  color?: String;
  hover?: Boolean;
  error?: Boolean;
  showConfirm?: Boolean;
}

const LOG_CONTEXT = { component: "GameControl" };

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

export function Item(props: BoxProps) {
  const { sx, ...other } = props;
  return (
    <Box
      sx={{
        bgcolor: "background.default",
        color: "text.primary",
        border: "1px solid",
        borderColor: "divider",
        p: 1,
        borderRadius: 2,
        textAlign: "center",
        fontSize: "0.875rem",
        fontWeight: "700",
        ...sx,
      }}
      {...other}
    />
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CreateRematchButton = (props: {
  game: Game;
  iconOnly?: boolean;
  buttonTitle?: string;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { game, iconOnly, buttonTitle } = props;

  return (
    <>
      {!iconOnly && !game.rematchId && (
        <Button
          onClick={() => createRematch(game!, navigate)}
          variant="contained"
          size="large"
          fullWidth
          sx={{
            py: 1.5,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            fontWeight: 600,
            fontSize: "1rem",
            "&:hover": {
              bgcolor: "primary.dark",
              transform: "translateY(-2px)",
            },
            transition: "all 0.3s",
          }}
        >
          {buttonTitle || t("gameControl:playAgainButton")}
        </Button>
      )}
      {!iconOnly && game.rematchId && (
        <Button
          href={`/game/${game.rematchId}`}
          variant="contained"
          size="large"
          fullWidth
          sx={{
            py: 1.5,
            bgcolor: "text.secondary",
            color: "primary.main",
            border: "2px solid primary.main",
            fontWeight: 600,
            fontSize: "1rem",
            "&:hover": {
              bgcolor: "text.secondary",
              transform: "translateY(-2px)",
            },
            transition: "all 0.3s",
          }}
        >
          {buttonTitle || t("gameControl:gotoRematchButton")}
        </Button>
      )}
    </>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function GameView(props: GameProps) {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { themeConfig } = useAppTheme();

  // ============================================================================
  // STATE
  // ============================================================================

  const [stoneSet, setStoneSet] = React.useState<Stone[]>();
  const [game, setGame] = React.useState<Game>();
  const [series, setSeries] = React.useState<GameSeries>();
  const [stone, setStone] = React.useState<StoneType>();
  const [gameNotFound, setGameNotFound] = React.useState(false);
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [winRow, setWinRow] = React.useState<number[]>();
  const [reload, setReload] = React.useState(false);
  const [player, setPlayer] = React.useState<number>();
  const [showConfetti, setShowConfetti] = React.useState(false);
  const [namespacesLoaded, setNamespacesLoaded] = React.useState({
    gameControl: false,
    shareGame: false,
    StoneSelectToolTips: false,
  });

  logger.debug("GameView rendered", {
    ...LOG_CONTEXT,
    gameId: params.gameId,
    playerId: player,
  });

  // ============================================================================
  // GAME INITIALIZATION
  // ============================================================================

  const initGame = React.useCallback(async (gameId?: string) => {
    logger.info("Initializing game", {
      ...LOG_CONTEXT,
      method: "initGame",
      gameId,
    });

    try {
      // Load stones
      const tmpStoneSet = await getAllStones();
      setStoneSet(tmpStoneSet);
      logger.success(`Loaded ${tmpStoneSet.length} stones`, {
        ...LOG_CONTEXT,
        method: "initGame",
      });

      // Load game
      if (!gameId && !game?.id) {
        logger.warn("No gameId provided", {
          ...LOG_CONTEXT,
          method: "initGame",
        });
        return;
      }

      const targetGameId = gameId || game!.id;
      logger.info("Fetching game", {
        ...LOG_CONTEXT,
        method: "initGame",
        gameId: targetGameId,
      });

      const gameTmp = await getGameById(targetGameId);

      if (!gameTmp) {
        logger.error("Game not found", {
          ...LOG_CONTEXT,
          method: "initGame",
          gameId: targetGameId,
        });
        setGameNotFound(true);
        return;
      }

      logger.success("Game loaded", {
        ...LOG_CONTEXT,
        method: "initGame",
        gameId: targetGameId,
      });

      // Add to local storage
      addGameToLocalStorage(gameTmp);

      // Check for win condition
      const winRowCheck = hasWon(gameTmp.board!, gameTmp.expertMode);
      if (winRowCheck && Array.isArray(winRowCheck)) {
        logger.info(
          "Game has a winner",
          { ...LOG_CONTEXT, method: "initGame" },
          { winRow: winRowCheck }
        );
        setWinRow(winRowCheck);
      }

      // Check players
      await checkPlayers(gameTmp);

      // Set current stone
      if (gameTmp.currentStone) {
        logger.debug("Setting current stone", {
          ...LOG_CONTEXT,
          method: "initGame",
        });
        setStone(gameTmp.currentStone as StoneType);
      } else {
        setStone(undefined);
      }

      // Load series if exists
      if (gameTmp.seriesId) {
        const seriesTmp = await getGameSeriesById(gameTmp.seriesId);
        if (seriesTmp) {
          setSeries(seriesTmp);
        }
      }

      // Update state
      setGame({ ...gameTmp });
      if (gameTmp.winRow) {
        setWinRow(gameTmp.winRow as number[]);
      }
    } catch (error) {
      logger.error(
        "Failed to initialize game",
        { ...LOG_CONTEXT, method: "initGame", gameId },
        error
      );
      setGameNotFound(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - we call this manually with gameId parameter

  // ============================================================================
  // PLAYER MANAGEMENT
  // ============================================================================

  const selectPlayer = React.useCallback(
    async (playerNumber: string, game: Game, name?: string) => {
      logger.info(
        "Selecting player",
        {
          ...LOG_CONTEXT,
          method: "selectPlayer",
          playerId: Number(playerNumber),
        },
        { name }
      );

      const numPlayer = Number(playerNumber);

      // Save to localStorage
      localStorage.setItem(game.id, JSON.stringify({ player: playerNumber }));
      void persistLocalStateToCloud();

      // Update game with player name
      const updates: any = {};
      if (numPlayer === 1) {
        updates.player1 = name || t("CreateGameDefaults.player1");
      } else if (numPlayer === 2) {
        updates.player2 = name || t("CreateGameDefaults.player2");
      }

      await updateGameInDB(game.id, updates as Partial<Game>, setGame);
      setPlayer(numPlayer);

      logger.success("Player selected", {
        ...LOG_CONTEXT,
        method: "selectPlayer",
        playerId: numPlayer,
      });
    },
    [t]
  );

  const checkPlayers = React.useCallback(async (game: Game) => {
    logger.info("Checking players", {
      ...LOG_CONTEXT,
      method: "checkPlayers",
      gameId: game.id,
    });

    // PRIORITY 1: Check if logged-in user matches player1UserId or player2UserId
    try {
      const { Auth } = await import("aws-amplify");
      const user = await Auth.currentAuthenticatedUser();
      const userId = user.attributes.sub;

      const gameData = game as any; // Cast to access new fields

      if (gameData.player1UserId === userId) {
        logger.info("✅ Logged-in user is Player 1 (via userId)", {
          ...LOG_CONTEXT,
          method: "checkPlayers",
          playerId: 1,
        });
        setPlayer(1);
        // Also save to localStorage for consistency
        localStorage.setItem(game.id, JSON.stringify({ player: 1 }));
        return; // Done!
      } else if (gameData.player2UserId === userId) {
        logger.info("✅ Logged-in user is Player 2 (via userId)", {
          ...LOG_CONTEXT,
          method: "checkPlayers",
          playerId: 2,
        });
        setPlayer(2);
        // Also save to localStorage for consistency
        localStorage.setItem(game.id, JSON.stringify({ player: 2 }));
        return; // Done!
      }

      logger.debug("ℹ️ Logged-in user, but not assigned to this game yet", {
        ...LOG_CONTEXT,
        method: "checkPlayers",
      });
    } catch {
      logger.debug("ℹ️ Not logged in, checking localStorage", {
        ...LOG_CONTEXT,
        method: "checkPlayers",
      });
    }

    // PRIORITY 2: Check localStorage (for anonymous users or legacy games)
    const gameFromStorage = JSON.parse(localStorage.getItem(game.id) || "{}");
    const storedPlayer = gameFromStorage.player;

    if (storedPlayer) {
      logger.debug("Found player in localStorage", {
        ...LOG_CONTEXT,
        method: "checkPlayers",
        playerId: Number(storedPlayer),
      });
      setPlayer(Number(storedPlayer));
    } else {
      logger.debug("No player in localStorage", {
        ...LOG_CONTEXT,
        method: "checkPlayers",
      });

      // PRIORITY 3: Handle rematch scenario
      if (game?.rejoinedPlayers && game?.rejoinedPlayers.length === 1) {
        logger.info("Rematch detected, assigning remaining player", {
          ...LOG_CONTEXT,
          method: "checkPlayers",
        });

        const newPlayer = game.rejoinedPlayers[0] === 1 ? 2 : 1;
        setPlayer(newPlayer);
        localStorage.setItem(game.id, JSON.stringify({ player: newPlayer }));
        void persistLocalStateToCloud();

        await updateGameInDB(
          game.id,
          { rejoinedPlayers: [...game.rejoinedPlayers, newPlayer] },
          setGame
        );

        logger.success("Rematch player assigned", {
          ...LOG_CONTEXT,
          method: "checkPlayers",
          playerId: newPlayer,
        });
      }
    }

    // Ensure current player is set
    if (game?.player1 && game?.player2 && !game?.currentPlayer) {
      logger.info("Setting initial current player", {
        ...LOG_CONTEXT,
        method: "checkPlayers",
      });
      await updateGameInDB(game.id, { currentPlayer: 1 }, setGame);
    }
  }, []);

  // ============================================================================
  // STONE MANAGEMENT
  // ============================================================================

  const handleStoneSelection = React.useCallback(
    (value: StoneType) => {
      console.log("🎯 [GameControl] handleStoneSelection called with:", {
        key: value.key,
        name: value.name,
        color: value.color,
        size: value.size,
        shape: value.shape,
        variant: value.variant,
        fullStone: value,
      });
      logger.info(
        "Stone selected",
        { ...LOG_CONTEXT, method: "handleStoneSelection" },
        { stone: value }
      );

      setStone(value);
      if (game) {
        console.log("💾 [GameControl] Saving currentStone to DB:", value);
        updateGameInDB(game.id, { currentStone: value as StoneType }, setGame);
      }
    },
    [game]
  );

  const placeStone = React.useCallback(
    async (index: number) => {
      if (!game) {
        logger.warn("Cannot place stone: missing game", {
          ...LOG_CONTEXT,
          method: "placeStone",
        });
        return;
      }

      // CRITICAL: Always use game.currentStone, not the local stone state!
      // The local stone state might be out of sync due to subscriptions
      const stoneToPlace = game.currentStone || stone;

      if (!stoneToPlace) {
        logger.warn(
          "Cannot place stone: no currentStone in game",
          {
            ...LOG_CONTEXT,
            method: "placeStone",
          },
          {
            gameCurrentStone: game.currentStone,
            localStone: stone,
          }
        );
        return;
      }

      console.log("🎯 [GameControl] placeStone called:", {
        index,
        usingGameCurrentStone: !!game.currentStone,
        usingLocalStone: !game.currentStone && !!stone,
        stoneKey: stoneToPlace.key,
        stoneName: stoneToPlace.name,
        stoneColor: stoneToPlace.color,
        stoneSize: stoneToPlace.size,
        stoneShape: stoneToPlace.shape,
        stoneVariant: stoneToPlace.variant,
        fullStone: stoneToPlace,
        boardIndex: index,
        currentBoardField: game.board![index],
        gameCurrentStone: game.currentStone,
        localStone: stone,
      });

      logger.info(
        "Placing stone",
        {
          ...LOG_CONTEXT,
          method: "placeStone",
          gameId: game.id,
          playerId: game.currentPlayer || undefined,
        },
        {
          index,
          stone: stoneToPlace,
        }
      );

      try {
        // Create updated board - use stoneToPlace, not stone!
        const boardField: BoardField = {
          ...game.board![index],
          stone: stoneToPlace,
        };

        console.log("🎯 [GameControl] Created boardField:", {
          index,
          stoneKey: boardField.stone?.key,
          stoneName: boardField.stone?.name,
          stoneColor: boardField.stone?.color,
          stoneSize: boardField.stone?.size,
          stoneShape: boardField.stone?.shape,
          stoneVariant: boardField.stone?.variant,
          boardField,
        });

        const newBoard = [...game.board!];
        newBoard[index] = boardField;

        const currentPlayer = game.currentPlayer || 1;
        const nextPlayer = getNextPlayer(currentPlayer);

        logger.debug(
          "Board updated",
          { ...LOG_CONTEXT, method: "placeStone" },
          {
            index,
            currentPlayer,
            nextPlayer,
          }
        );

        // Prepare update
        let updatedGameFields: Partial<Game> = {
          board: newBoard,
          currentPlayer: nextPlayer,
          currentStone: null,
          gameHasStarted: true,
        };

        // Check for win
        const winRowCheck = hasWon(newBoard, game.expertMode);
        if (winRowCheck && Array.isArray(winRowCheck)) {
          const currentPlayerName = getPlayerName(
            currentPlayer!,
            game.player1,
            game.player2
          );

          logger.success(
            "🏆 GAME WON!",
            { ...LOG_CONTEXT, method: "placeStone" },
            {
              winner: currentPlayerName,
              winRow: winRowCheck,
            }
          );

          updatedGameFields = {
            ...updatedGameFields,
            winner: currentPlayerName,
            winRow: winRowCheck as number[],
          };
        }

        // OPTIMISTIC UPDATE: Update UI immediately before API call
        // This ensures the stone appears instantly, even if the API call is slow
        console.log(
          "⚡ [GameControl] Optimistic update - showing stone immediately"
        );
        setGame({
          ...game,
          ...updatedGameFields,
        });

        if (winRowCheck && Array.isArray(winRowCheck)) {
          setWinRow(winRowCheck as number[]);
        }

        // Update game in database (this will also trigger subscription update)
        const updatedGame = await updateGameInDB(
          game.id,
          updatedGameFields,
          setGame
        );

        // CRITICAL: Ensure the board is updated even if API response doesn't include it
        // Sometimes the API response might not include the full board, so we merge it
        if (updatedGame && updatedGame.board) {
          console.log(
            "✅ [GameControl] API response includes board, updating state"
          );
          // The setGame callback in updateGameInDB should have already updated it,
          // but let's make sure the board is correct
          setGame((prevGame) => {
            if (!prevGame) return prevGame;
            // Only update if the board from API has the same or more stones
            const apiBoardStones =
              updatedGame.board?.filter((f: any) => f?.stone)?.length || 0;
            const currentBoardStones =
              prevGame.board?.filter((f: any) => f?.stone)?.length || 0;

            if (apiBoardStones >= currentBoardStones) {
              console.log("✅ [GameControl] API board is newer, using it");
              return { ...prevGame, board: updatedGame.board };
            } else {
              console.log(
                "⚠️ [GameControl] Current board has more stones, keeping it"
              );
              return prevGame;
            }
          });
        } else {
          console.log(
            "⚠️ [GameControl] API response missing board, keeping optimistic update"
          );
        }

        // Clear current stone
        setStone(undefined);

        logger.success("Stone placed successfully", {
          ...LOG_CONTEXT,
          method: "placeStone",
        });
      } catch (error) {
        logger.error(
          "Failed to place stone",
          { ...LOG_CONTEXT, method: "placeStone", gameId: game.id },
          error
        );
      }
    },
    [game, stone] // Keep stone in deps for fallback, but prefer game.currentStone
  );

  // ============================================================================
  // GAME CLAIMING
  // ============================================================================

  const handleClaimGame = React.useCallback(async () => {
    if (!game) return;

    try {
      logger.info("Claiming game", { ...LOG_CONTEXT, gameId: game.id });

      const { claimGame } = await import("../api/gameApi");
      await claimGame(game.id!);

      // Refresh game to get updated owner/sharedWith
      const updated = await getGameById(game.id!);
      if (updated) {
        setGame(updated);
      }

      logger.success("Game claimed successfully", {
        ...LOG_CONTEXT,
        gameId: game.id,
      });
    } catch (error) {
      logger.error(
        "Failed to claim game",
        { ...LOG_CONTEXT, gameId: game.id },
        error
      );
    }
  }, [game]);

  // ============================================================================
  // GAME SETTINGS
  // ============================================================================

  const updateGame = React.useCallback(
    async (data: {
      title?: string;
      description?: string;
      iconSet?: number;
      expertMode?: boolean;
    }) => {
      if (!game) return;

      logger.info(
        "Updating game settings",
        { ...LOG_CONTEXT, method: "updateGame", gameId: game.id },
        data
      );

      const updates: any = {};
      if (data.title) updates.title = data.title;
      if (data.description) updates.description = data.description;
      if (data.iconSet !== undefined) updates.iconSet = data.iconSet;
      if (data.expertMode !== undefined) updates.expertMode = data.expertMode;

      if (Object.keys(updates).length > 0) {
        await updateGameInDB(game.id, updates as Partial<Game>, setGame);
        logger.success("Game settings updated", {
          ...LOG_CONTEXT,
          method: "updateGame",
          gameId: game.id,
        });
      }
    },
    [game]
  );

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Load namespaces
  React.useEffect(() => {
    logger.debug("Loading i18n namespaces", { ...LOG_CONTEXT });

    const loadNamespaces = async () => {
      try {
        await i18next.loadNamespaces([
          "gameControl",
          "shareGame",
          "StoneSelectToolTips",
        ]);
        setNamespacesLoaded({
          gameControl: true,
          shareGame: true,
          StoneSelectToolTips: true,
        });
        logger.success("i18n namespaces loaded", { ...LOG_CONTEXT });
      } catch (err) {
        logger.error("Failed to load i18n namespaces", { ...LOG_CONTEXT }, err);
      }
    };

    loadNamespaces();
  }, []);

  // Initialize game and set up subscription with connection-aware refetch
  React.useEffect(() => {
    if (!params.gameId) {
      logger.warn("No gameId in params", { ...LOG_CONTEXT });
      return;
    }

    logger.info("Setting up game", { ...LOG_CONTEXT, gameId: params.gameId });

    // Handle reload
    if (reload) {
      logger.info("Reloading page", { ...LOG_CONTEXT });
      setReload(false);
      window.location.reload();
      return;
    }

    // Initialize game
    initGame(params.gameId);

    // Subscribe to game updates via GraphQL (real-time)
    const subscription = subscribeToGame(params.gameId, (updatedGame) => {
      logger.info(
        "🔔 REALTIME UPDATE via subscription!",
        { ...LOG_CONTEXT, gameId: params.gameId },
        {
          currentPlayer: updatedGame.currentPlayer,
          version: (updatedGame as any)._version,
          winner: updatedGame.winner,
          currentStone: updatedGame.currentStone?.key,
        }
      );
      console.log("🔔 SUBSCRIPTION UPDATE:", {
        ...updatedGame,
        currentStoneKey: updatedGame.currentStone?.key,
        currentStoneName: updatedGame.currentStone?.name,
        boardLength: updatedGame.board?.length,
        boardFieldsWithStones: updatedGame.board?.filter((f: any) => f?.stone)
          ?.length,
        boardFields: updatedGame.board?.map((f: any, i: number) => ({
          index: i,
          key: f?.key,
          hasStone: !!f?.stone,
          stoneKey: f?.stone?.key,
        })),
      });

      // Validate board is present and has correct length
      if (!updatedGame.board || updatedGame.board.length !== 16) {
        console.error(
          "❌ [Subscription] Invalid board in subscription update:",
          {
            boardLength: updatedGame.board?.length,
            expectedLength: 16,
          }
        );
        // Don't update if board is invalid
        return;
      }

      // CRITICAL: Sync stone state with game.currentStone from subscription
      if (updatedGame.currentStone) {
        console.log(
          "🔄 [Subscription] Syncing stone state:",
          updatedGame.currentStone
        );
        setStone(updatedGame.currentStone as StoneType);
      } else {
        console.log(
          "🔄 [Subscription] Clearing stone state (currentStone is null)"
        );
        setStone(undefined);
      }

      // CRITICAL: Always update game state from subscription to ensure UI is in sync
      // But be smart about it - don't overwrite with older data
      console.log("🔄 [Subscription] Updating game state with board:", {
        boardLength: updatedGame.board?.length,
        boardFieldsWithStones: updatedGame.board?.filter((f: any) => f?.stone)
          ?.length,
        subscriptionVersion: (updatedGame as any)._version,
      });

      setGame((prevGame) => {
        if (!prevGame) {
          console.log(
            "🔄 [Subscription] No previous game, using subscription data"
          );
          return { ...updatedGame };
        }

        // Check if subscription has a newer version
        const prevVersion = (prevGame as any)._version || 0;
        const subVersion = (updatedGame as any)._version || 0;

        if (subVersion >= prevVersion) {
          // Subscription is newer or same, use it
          console.log(
            "🔄 [Subscription] Subscription is newer (or same), using it",
            {
              prevVersion,
              subVersion,
              prevBoardStones:
                prevGame.board?.filter((f: any) => f?.stone)?.length || 0,
              subBoardStones:
                updatedGame.board?.filter((f: any) => f?.stone)?.length || 0,
            }
          );
          return { ...updatedGame };
        } else {
          // Subscription is older, keep current state but merge non-board fields
          console.log(
            "⚠️ [Subscription] Subscription is older, keeping current board",
            {
              prevVersion,
              subVersion,
            }
          );
          return {
            ...prevGame,
            ...updatedGame,
            board: prevGame.board, // Keep current board
          };
        }
      });
    });

    // Handle window focus (reload data)
    const handleFocus = () => {
      logger.info("Window focused, refreshing game", {
        ...LOG_CONTEXT,
        gameId: params.gameId,
      });
      initGame(params.gameId);
    };

    // Handle network reconnection (refetch game data)
    const handleOnline = () => {
      logger.info("🌐 Network: ONLINE - Refetching game data", {
        ...LOG_CONTEXT,
        gameId: params.gameId,
      });
      console.log("🌐 Network: ONLINE - Refetching game");
      // Refetch game to get latest state after reconnection
      initGame(params.gameId);
    };

    const handleOffline = () => {
      logger.warn("📵 Network: OFFLINE", {
        ...LOG_CONTEXT,
        gameId: params.gameId,
      });
      console.log("📵 Network: OFFLINE");
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Cleanup
    return () => {
      logger.debug("🛑 Cleaning up game subscription", {
        ...LOG_CONTEXT,
        gameId: params.gameId,
      });
      console.log("🛑 SUBSCRIPTION STOPPED for game:", params.gameId);
      subscription.unsubscribe();
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.gameId, reload]); // initGame is stable due to useCallback with empty deps

  // Cleanup anonymous game when it finishes
  React.useEffect(() => {
    if (game?.winner) {
      // Game has finished, remove from anonymous tracking
      const cleanup = async () => {
        try {
          const { cleanupFinishedGame } = await import(
            "../utils/anonymousGames"
          );
          cleanupFinishedGame(game.id!);
        } catch {
          // Not an anonymous game or already cleaned
        }
      };
      cleanup();
    }
  }, [game?.winner, game?.id]);

  // 🎉 Trigger confetti when game is won!
  const triggerConfetti = React.useCallback(() => {
    if (game?.winner && player) {
      const myPlayerName = getPlayerName(player, game.player1, game.player2);
      // Only show confetti if YOU won
      if (myPlayerName === game.winner) {
        console.log("🎉 YOU WON! Triggering confetti!");
        setShowConfetti(true);
      }
    }
  }, [game?.winner, player, game?.player1, game?.player2]);

  // Automatically trigger confetti every 10 seconds when YOU won
  React.useEffect(() => {
    if (game?.winner && player) {
      const myPlayerName = getPlayerName(player, game.player1, game.player2);
      // Only set interval if YOU won
      if (myPlayerName === game.winner) {
        // Trigger immediately
        triggerConfetti();

        // Then every 10 seconds
        const interval = setInterval(() => {
          triggerConfetti();
        }, 10000);

        return () => clearInterval(interval);
      }
    }
  }, [game?.winner, player, game?.player1, game?.player2, triggerConfetti]);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const allNamespacesLoaded = Object.values(namespacesLoaded).every(Boolean);
  const gameStarted = game?.player1 && game?.player2 && stoneSet !== undefined;
  const myTurn = gameStarted && player === game?.currentPlayer;

  const gameInfoHeaderProps = {
    game: game!,
    gameStarted: !!gameStarted,
    myTurn: !!myTurn,
    player: player,
    stone: stone,
    series: series,
    updateGame: updateGame,
    selectPlayer: selectPlayer,
  };

  logger.debug(
    "Render state",
    { ...LOG_CONTEXT },
    {
      gameStarted,
      myTurn,
      hasWinner: !!game?.winner,
      player,
      allNamespacesLoaded,
    }
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  // Game not found
  if (!game && gameNotFound) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        p={3}
      >
        <Card
          elevation={8}
          sx={{
            maxWidth: 500,
            width: "100%",
            background: themeConfig.dialog.background,
            border: themeConfig.dialog.border,
            borderRadius: 3,
            textAlign: "center",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <SentimentDissatisfiedIcon
              sx={{
                fontSize: 80,
                color: "text.secondary",
                mb: 2,
                opacity: 0.6,
              }}
            />
            <Typography
              variant="h4"
              fontWeight={600}
              color="primary.main"
              gutterBottom
            >
              {t("gameControl:gameNotFoundTitle")}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t("gameControl:gameNotFound", { gameId: params.gameId })}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 4, fontStyle: "italic" }}
            >
              {t("gameControl:gameNotFoundHelp")}
            </Typography>
            <Box display="flex" gap={2} justifyContent="center">
              <Button
                variant="outlined"
                onClick={() => navigate("/")}
                sx={{
                  borderColor: "divider",
                  color: "text.primary",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "action.hover",
                  },
                }}
              >
                {t("gameControl:backToGames")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setCreateDialogOpen(true)}
                sx={{
                  fontWeight: 600,
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.2s",
                }}
              >
                {t("gameControl:createNewGameButton")}
              </Button>
            </Box>
          </CardContent>
        </Card>

        <CreateGameDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          count={0}
        />
      </Box>
    );
  }

  // Loading
  if (!game || !allNamespacesLoaded) {
    return <></>;
  }

  // Need to choose player
  if (gameStarted && !player) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="flex-start"
        sx={{
          minHeight: "100vh",
          background: "background.default",
        }}
      >
        <GameInfoHeaderControl {...gameInfoHeaderProps} />
      </Box>
    );
  }

  // Main game view
  return (
    <Box
      width="100%"
      display={"flex"}
      alignItems="center"
      flexDirection={"column"}
      pb={1}
      mb={1}
    >
      {/* Game not started - show only share link */}
      {!gameStarted && !game.winner && player && (
        <ShareGameLinkControl game={game} />
      )}

      {/* No player selected yet */}
      {!player && !game.winner && (
        <GameInfoHeaderControl editMode={true} {...gameInfoHeaderProps} />
      )}

      {/* Game in progress or finished */}
      {gameStarted && (
        <>
          <Box
            m={0}
            pb={1}
            width="100%"
            display={"flex"}
            alignItems="center"
            flexDirection={"column"}
          >
            {/* Claim Game Hint (if logged in and game is anonymous) */}
            {game && (
              <ClaimGameHint gameId={game.id!} onClaim={handleClaimGame} />
            )}

            {/* Game Board */}
            <GameBoardControl
              game={game}
              setGame={setGame}
              myTurn={!!myTurn}
              player={player}
              stone={stone}
              placeStone={placeStone}
              winRow={winRow}
              updateGame={updateGame}
            />

            {/* Remaining Stones */}
            {!game.winner && stoneSet && (
              <RemainingStonesControl game={game} stones={stoneSet} />
            )}
          </Box>

          {/* Stone Selection Dialog */}
          {!game.winner && stoneSet && (
            <SelectStoneDialog
              open={!myTurn && !stone}
              onClose={handleStoneSelection}
              game={game}
              stones={stoneSet}
              myTurn={myTurn || false}
              player={player || undefined}
            />
          )}
        </>
      )}

      {/* 🎉 Confetti Animation */}
      <Confetti
        active={showConfetti}
        onComplete={() => setShowConfetti(false)}
      />
    </Box>
  );
}
